/**
 * AdjacencyMap — primary SVG canvas for the interactive adjacency map.
 *
 * Dark-themed canvas with radial blue glow, glassmorphism overlays,
 * SVG filter defs for hover/select glow effects.
 *
 * Uses a fixed 4000×4000 logical canvas (SVG coordinate space).
 * Pan/zoom via d3-zoom maps the logical canvas to the viewport.
 * Fit-to-content on first load shows the entire map.
 *
 * Layers (back to front):
 *   1. Dark background with radial glow gradient
 *   2. Ring circles with labels
 *   3. Axis lines with labels
 *   4. Core label
 *   5. Connection lines (renders nothing when edges are empty)
 *   6. MapNode components (zoom-responsive, scale with zoom)
 */

import { useMemo, useEffect, useRef, useState, useCallback, type ChangeEvent, type CSSProperties } from 'react';
import { useAdjacencyStore } from '../../store/adjacency-store';
import type {
  AdjacencyAxis,
  RelatednessRing,
} from '../../types/adjacency';
import { RING_SUCCESS_RATES } from '../../types/adjacency';
import {
  computeAllPositions,
  getRingRadius,
  getAxisEndpoint,
  getAxisLabelPosition,
  RING_BANDS,
  LOGICAL_CANVAS_SIZE,
  CORE_CIRCLE_RADIUS,
} from '../../utils/positioning';
import type { CanvasSize } from '../../utils/positioning';
import { useMapTransform } from '../../hooks/useMapTransform';
import { MapNode } from './MapNode';
import { ConnectionLines } from './ConnectionLines';
import { MapControls } from './MapControls';
// MapLegend is now rendered by App.tsx for all views
import { NodeContextMenu } from './NodeContextMenu';
import { NodeModal } from '../editors/NodeModal';
import { LineAttributeModal } from '../editors/LineAttributeModal';
import { BulkImportModal } from '../editors/BulkImportModal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { radiusToRing } from '../../utils/position-to-ring';
import { INITIATIVE_COLORS, STATUS_COLORS, STATUS_DEFAULT_COLOR } from '../../utils/map-colors';

// ── Constants ────────────────────────────────────────────────────────

/** Fixed logical canvas — node positions live in this coordinate space. */
const canvasSize: CanvasSize = { width: LOGICAL_CANVAS_SIZE, height: LOGICAL_CANVAS_SIZE };
const canvasRadius = LOGICAL_CANVAS_SIZE / 2;

/** Rings to render (excluding ring 0 which is just the center point). */
const RINGS: RelatednessRing[] = [1, 2, 3, 4];

/** The 8 non-Core axes for rendering lines and labels (CW from top). */
const MAP_AXES: AdjacencyAxis[] = [
  'New Services',
  'New Markets & Customers',
  'Forward Integration',
  'New Technologies & Capabilities',
  'New Channels',
  'New Geographies',
  'Backward Integration',
  'New Products',
];

// ── Filter Logic ─────────────────────────────────────────────────────

import { nodePassesFilter } from '../../utils/filter-nodes';

// ── Component ────────────────────────────────────────────────────────

export function AdjacencyMap() {
  const {
    nodes, filters, setFilters, selectedNodeId, selectNode,
    selectedEdge, selectEdge,
    contextMenu, openContextMenu, closeContextMenu,
    createParentChildLink, createRelatedLink, removeRelationship, convertEdgeType,
    updateEdgeAttributes, updateNode, deleteNode, duplicateNode, createNode, bulkCreateNodes,
    importMapData,
    _past, _future, undo, redo,
  } = useAdjacencyStore();
  const { transformRef, transform, zoomIn, zoomOut, resetZoom, fitToContent } = useMapTransform();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasFittedRef = useRef(false);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hoveredAxis, setHoveredAxis] = useState<AdjacencyAxis | null>(null);
  const [axisHoverPos, setAxisHoverPos] = useState<{ x: number; y: number } | null>(null);
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const [lineEditEdge, setLineEditEdge] = useState<{ sourceId: string; targetId: string } | null>(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    confirmColor?: string;
    onConfirm: () => void;
  } | null>(null);

  // ── Drag-to-reposition state ────────────────────────────────────────
  const [dragState, setDragState] = useState<{
    nodeId: string;
    /** Current position in SVG logical coords (center 0,0). */
    pos: { x: number; y: number };
    /** Original computed position for ghost rendering. */
    origin: { x: number; y: number };
    /** WQ-339: cursor offset from the node centre at grab time, so the node moves
     *  with the hand exactly as grabbed (no jump to centre under the cursor). */
    grab: { x: number; y: number };
  } | null>(null);
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;
  const transformRef2 = useRef(transform);
  transformRef2.current = transform;

  const handleHoverStart = useCallback((id: string) => setHoveredNodeId(id), []);
  const handleHoverEnd = useCallback(() => setHoveredNodeId(null), []);

  const handleAxisMouseMove = useCallback((axis: AdjacencyAxis, e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHoveredAxis(axis);
    setAxisHoverPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);
  const handleAxisMouseLeave = useCallback(() => {
    setHoveredAxis(null);
    setAxisHoverPos(null);
  }, []);
  const handleNodeClick = useCallback((id: string, e?: React.MouseEvent) => {
    // Ctrl+Click or Alt+Click: auto-detect link type by axis
    if ((e?.ctrlKey || e?.altKey) && selectedNodeId && selectedNodeId !== id) {
      const sourceNode = nodes.find(n => n.id === selectedNodeId);
      const targetNode = nodes.find(n => n.id === id);
      if (sourceNode && targetNode) {
        if (sourceNode.adjacencyAxis === targetNode.adjacencyAxis) {
          createParentChildLink(selectedNodeId, id);
        } else {
          createRelatedLink(selectedNodeId, id);
        }
      }
      return;
    }
    // Regular click: toggle selection
    selectNode(selectedNodeId === id ? null : id);
  }, [selectNode, selectedNodeId, nodes, createParentChildLink, createRelatedLink]);
  const handleNodeDoubleClick = useCallback((id: string) => {
    selectNode(id);
    setDetailNodeId(id);
  }, [selectNode]);
  const handleNodeContextMenu = useCallback((id: string, e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    openContextMenu({ nodeId: id, x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, [openContextMenu]);
  const handleBackgroundClick = useCallback((e: React.MouseEvent) => {
    // Only deselect if the click target is the SVG background itself
    if ((e.target as SVGElement).tagName === 'svg' || (e.target as SVGElement).classList?.contains('map-bg-hit')) {
      selectNode(null);
      closeContextMenu();
    }
  }, [selectNode, closeContextMenu]);
  const handleEdgeDoubleClick = useCallback((edge: { sourceId: string; targetId: string }) => {
    setLineEditEdge(edge);
  }, []);
  const handleLineEditSave = useCallback((attrs: { lineStyle: 'solid' | 'dashed' | 'dotted'; lineColor: string; label: string }) => {
    if (!lineEditEdge) return;
    updateEdgeAttributes(lineEditEdge.sourceId, lineEditEdge.targetId, attrs);
    setLineEditEdge(null);
  }, [lineEditEdge, updateEdgeAttributes]);
  const handleEdgeClick = useCallback((edge: { sourceId: string; targetId: string }) => {
    // Toggle edge selection
    if (selectedEdge && selectedEdge.sourceId === edge.sourceId && selectedEdge.targetId === edge.targetId) {
      selectEdge(null);
    } else {
      selectEdge(edge);
    }
  }, [selectEdge, selectedEdge]);
  const handleCloseDetail = useCallback(() => {
    setDetailNodeId(null);
  }, []);

  // ── Add Node handler ────────────────────────────────────────────
  const handleAddNode = useCallback(async () => {
    // Spawn on New Services axis just above the core circle
    // mapPosition y=-0.1 → normalizedR ≈ 0.20 (ring 1 inner), angle = top (New Services)
    const newNode = await createNode({
      name: 'New Node',
      description: '',
      strategicInitiative: 'Other',
      phase: null,
      status: 'Not Started',
      priority: null,
      adjacencyAxis: 'New Services',
      relatednessRing: 1,
      sharedCustomers: 5,
      sharedCosts: 5,
      commonCompetitors: 5,
      attractivenessScore: 5,
      reinforcesCore: false,
      nodeType: 'product',
      source: 'manual',
      edges: [],
      parentIds: [],
      mapPosition: { x: 0, y: -0.1 },
      spPriorityTag: null,
      spSyncReviewStatus: null,
      spListItemId: null,
      lastSyncedAt: null,
      estRnD: null,
      estRevenue: null,
      iRnD: null,
      fRnD: null,
      totalBudgetEstimate: null,
      tam: null,
      sam: null,
      tamAiSuggested: null,
      samAiSuggested: null,
      startDate: null,
      endDate: null,
      mrdDate: null,
      mrdTargetDate: null,
      atdDate: null,
      atqDate: null,
      atsDate: null,
      ganttStartDate: null,
      ganttEndDate: null,
      fteAllocation: null,
      iwtSouth: false,
      iwtSouthFte: null,
      isMvp: false,
      devLane: null,
      devTeam: null,
      productManager: null,
      productLine: null,
      budgetProjectName: null,
      spSyncSnapshot: null,
    });
    selectNode(newNode.id);
    setDetailNodeId(newNode.id);
  }, [createNode, selectNode]);

  // ── Export / Import handlers ─────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = useCallback(() => {
    const json = JSON.stringify(nodes, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `iwt-adjacency-map-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [nodes]);

  const handleImportData = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        // WQ-340: accept an export file ({metadata, nodes}) or a bare node array.
        const importedNodes: unknown[] | null = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.nodes) ? parsed.nodes : null;
        if (!importedNodes) {
          setPendingConfirm({
            title: 'Invalid File',
            message: 'Expected an exported adjacency map (a JSON array of nodes or {"nodes": [...]}).',
            confirmLabel: 'OK',
            confirmColor: '#f59e0b',
            onConfirm: () => setPendingConfirm(null),
          });
          return;
        }
        setPendingConfirm({
          title: 'Import Data',
          message: `Import ${importedNodes.length} nodes? This replaces the whole map. A backup of the current map is downloaded first.`,
          confirmLabel: 'Import',
          confirmColor: '#3b82f6',
          onConfirm: () => {
            void importMapData(parsed);
            setPendingConfirm(null);
          },
        });
      } catch {
        setPendingConfirm({
          title: 'Parse Error',
          message: 'Failed to parse JSON file.',
          confirmLabel: 'OK',
          confirmColor: '#f59e0b',
          onConfirm: () => setPendingConfirm(null),
        });
      }
    };
    reader.readAsText(file);
    // Reset input so the same file can be re-imported
    e.target.value = '';
  }, [importMapData]);

  // Track viewport (container) size via ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setViewportSize({ width, height });
        }
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Inverse zoom factor — keeps structural elements (rings, axes, labels) at constant screen size
  const inv = 1 / transform.k;

  // Compute all node positions (logical canvas space)
  const positions = useMemo(
    () => computeAllPositions(nodes, canvasSize),
    [nodes],
  );

  // ── Drag-to-reposition handlers (need positions above) ────────────
  const handleDragStart = useCallback((id: string, e: React.MouseEvent) => {
    const origin = positions.get(id);
    if (!origin) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const logicalX = (screenX - transform.x) / transform.k;
    const logicalY = (screenY - transform.y) / transform.k;
    const grab = { x: logicalX - origin.x, y: logicalY - origin.y };
    setDragState({ nodeId: id, pos: { x: origin.x, y: origin.y }, origin, grab });
    selectNode(id);
  }, [positions, transform, selectNode]);

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const t = transformRef2.current;
      const logicalX = (screenX - t.x) / t.k;
      const logicalY = (screenY - t.y) / t.k;
      setDragState(prev => prev ? { ...prev, pos: { x: logicalX - prev.grab.x, y: logicalY - prev.grab.y } } : null);
    };

    const handleMouseUp = (e: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      // Convert SVG logical coords to normalized mapPosition
      const CORE_OFFSET = 0.12;
      const remapScale = 0.95 - CORE_OFFSET;
      const { x: sx, y: sy } = ds.pos;
      const svgR = Math.sqrt(sx * sx + sy * sy);

      // Ctrl+drag suppresses auto-reclassification
      const suppressAutoClassify = e.ctrlKey || e.metaKey;

      if (svgR < 1) {
        // Dropped at center → core
        const changes: Parameters<typeof updateNode>[1] = { mapPosition: { x: 0, y: 0 } };
        if (!suppressAutoClassify) {
          changes.relatednessRing = 0;
          // Axis not auto-assigned on drag — see WQ-038
        }
        void updateNode(ds.nodeId, changes);
      } else {
        const angle = Math.atan2(sy, sx);
        const normalizedR = svgR / canvasRadius;
        // WQ-339: pinned positions render at CORE_OFFSET + rawR * remapScale, so nothing
        // can sit inside the core circle except dead centre. A drop inside the circle
        // gave a negative rawR and flipped the node to the OPPOSITE side; clamp to the
        // circle's edge on the same side instead.
        const rawR = Math.max((normalizedR - CORE_OFFSET) / remapScale, 0.0005);
        const mx = rawR * Math.cos(angle);
        const my = rawR * Math.sin(angle);

        const changes: Parameters<typeof updateNode>[1] = { mapPosition: { x: mx, y: my } };

        if (!suppressAutoClassify) {
          // Auto-detect ring from normalized radius
          changes.relatednessRing = radiusToRing(normalizedR);
          // Axis not auto-assigned on drag — nodes are often positioned in a
          // different sector for spacing. Axis changes go through NodeModal
          // which checks edge mismatches via AxisChangeWarningDialog. See WQ-038.
        }

        void updateNode(ds.nodeId, changes);
      }
      setDragState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, updateNode]);

  // Include axis label positions in fit bounds so labels aren't clipped
  const fitPositions = useMemo(() => {
    const all = new Map(positions);
    for (const axis of MAP_AXES) {
      const lp = getAxisLabelPosition(axis, canvasRadius);
      all.set(`__axis_${axis}`, { x: lp.x, y: lp.y });
    }
    return all;
  }, [positions]);

  const handleFitToContent = useCallback(() => fitToContent(fitPositions, 140), [fitToContent, fitPositions]);

  // Build set of node IDs that pass the current filter
  const filterPassingIds = useMemo(() => {
    const passing = new Set<string>();
    for (const node of nodes) {
      if (nodePassesFilter(node, filters)) {
        passing.add(node.id);
      }
    }
    return passing;
  }, [nodes, filters]);

  // Fit all nodes + axis labels into viewport on first load (instant, no animation)
  useEffect(() => {
    if (hasFittedRef.current || fitPositions.size === 0) return;
    if (viewportSize.width === 0 || viewportSize.height === 0) return;
    hasFittedRef.current = true;
    // Use rAF to ensure SVG is laid out at real viewport size before measuring
    requestAnimationFrame(() => {
      fitToContent(fitPositions, 140, false);
    });
  }, [fitPositions, fitToContent, viewportSize]);

  // ── Search zoom-to-results ─────────────────────────────────────────
  const prevSearchRef = useRef(filters.searchText);

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== filters.searchText;
    prevSearchRef.current = filters.searchText;
    if (!searchChanged) return;

    // When search cleared, zoom to fit all
    if (!filters.searchText) {
      fitToContent(fitPositions, 140, true);
      return;
    }

    // Build positions map of only matching nodes
    const matchPositions = new Map<string, { x: number; y: number }>();
    for (const id of filterPassingIds) {
      const pos = positions.get(id);
      if (pos) matchPositions.set(id, pos);
    }

    // If matches exist, zoom to fit them
    if (matchPositions.size > 0) {
      fitToContent(matchPositions, 200, true);
    }
  }, [filterPassingIds, filters.searchText, positions, fitPositions, fitToContent]);

  // Keyboard shortcuts: Delete (selected line), Escape.
  // Ctrl+Z / Ctrl+Y are app-wide since WQ-340 (AdjacencyMapApp).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // WQ-339: ignore keys typed into text fields (Delete in a field must not
      // remove a selected line).
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      // WQ-339: a dialog is open — let it handle its own keys. Without this, Escape
      // here deselected the node, re-rendered NodeModal and unregistered its Escape
      // listener mid-event, so the first Escape never closed the editor.
      if (detailNodeId || lineEditEdge || showBulkImport || pendingConfirm) return;
      if (e.key === 'Delete' && selectedEdge) {
        const nodeA = nodes.find(n => n.id === selectedEdge.sourceId);
        const nodeB = nodes.find(n => n.id === selectedEdge.targetId);
        const nameA = nodeA?.name ?? selectedEdge.sourceId;
        const nameB = nodeB?.name ?? selectedEdge.targetId;
        const edgeCopy = { ...selectedEdge };
        setPendingConfirm({
          title: 'Remove Connection',
          message: `Remove connection between "${nameA}" and "${nameB}"?`,
          confirmLabel: 'Remove',
          onConfirm: () => {
            removeRelationship(edgeCopy.sourceId, edgeCopy.targetId);
            selectEdge(null);
            setPendingConfirm(null);
          },
        });
      }
      // Escape: dismiss context menu first, then deselect edge, then node
      if (e.key === 'Escape') {
        if (contextMenu) closeContextMenu();
        else if (selectedEdge) selectEdge(null);
        else if (selectedNodeId) selectNode(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEdge, selectedNodeId, contextMenu, nodes, removeRelationship, selectEdge, selectNode, closeContextMenu, detailNodeId, lineEditEdge, showBulkImport, pendingConfirm]);

  // Detail modal node
  const detailNode = detailNodeId ? nodes.find(n => n.id === detailNodeId) ?? null : null;

  return (
    <div
      ref={containerRef}
      className="flex-1 min-h-0 rounded-xl overflow-hidden relative"
      style={{ background: 'var(--canvas-bg)' }}
    >
      <svg
        ref={transformRef}
        width={viewportSize.width || '100%'}
        height={viewportSize.height || '100%'}
        className="block select-none"
        onClick={handleBackgroundClick}
      >
        {/* ── SVG Defs: gradients & filters ────────────────── */}
        <defs>
          {/* Radial glow from center */}
          <radialGradient id="canvas-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.15} />
            <stop offset="60%" stopColor="#3b82f6" stopOpacity={0.04} />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
          </radialGradient>

          {/* Glassmorphism backdrop filter for nodes */}
          <filter id="glass-blur" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
          </filter>

          {/* Glass highlight — top-down frosted light crescent on nodes */}
          <linearGradient id="glass-highlight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity={0.30} />
            <stop offset="60%" stopColor="#ffffff" stopOpacity={0.05} />
            <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
          </linearGradient>

          {/* Band glassmorphism — blue gradient, darker inner → lighter outer */}
          {RINGS.map((ring) => {
            const band = RING_BANDS[ring];
            const innerFrac = band.inner;
            const outerFrac = band.outer;
            const blueTints: Record<number, string> = {
              1: '50,110,255',   // intense blue
              2: '80,150,255',   // vivid blue
              3: '110,185,255',  // bright cyan-blue
              4: '155,210,255',  // luminous sky blue
            };
            const tint = blueTints[ring] ?? '90,160,255';
            return (
              <radialGradient key={`band-grad-${ring}`} id={`band-grad-${ring}`} cx="50%" cy="50%" r="50%">
                <stop offset={`${innerFrac * 100}%`} stopColor={`rgb(${tint})`} stopOpacity={0} />
                <stop offset={`${(innerFrac + 0.01) * 100}%`} stopColor={`rgb(${tint})`} stopOpacity={0.18} />
                <stop offset={`${((innerFrac + outerFrac) / 2) * 100}%`} stopColor={`rgb(${tint})`} stopOpacity={0.11} />
                <stop offset={`${(outerFrac - 0.01) * 100}%`} stopColor={`rgb(${tint})`} stopOpacity={0.18} />
                <stop offset={`${outerFrac * 100}%`} stopColor={`rgb(${tint})`} stopOpacity={0} />
              </radialGradient>
            );
          })}

          {/* Glow filter for hovered nodes — soft colored aura */}
          <filter id="glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feColorMatrix
              in="blur"
              type="saturate"
              values="2"
              result="saturated"
            />
            <feMerge>
              <feMergeNode in="saturated" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Stronger glow for selected/active nodes */}
          <filter id="glow-strong" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
            <feColorMatrix
              in="blur"
              type="saturate"
              values="3"
              result="saturated"
            />
            <feMerge>
              <feMergeNode in="saturated" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
          {/* ── Background hit area for deselect ───────────── */}
          <rect
            className="map-bg-hit"
            x={-canvasRadius * 1.5}
            y={-canvasRadius * 1.5}
            width={canvasRadius * 3}
            height={canvasRadius * 3}
            fill="transparent"
          />

          {/* ── Layer 0: Radial glow background ────────────── */}
          <circle
            cx={0}
            cy={0}
            r={canvasRadius * 1.2}
            fill="url(#canvas-glow)"
          />

          {/* ── Layer 0.5: Ring band glassmorphism shading ── */}
          {RINGS.map((ring) => {
            const band = RING_BANDS[ring];
            const innerR = band.inner * canvasRadius;
            const outerR = band.outer * canvasRadius;

            const annularPath = [
              `M 0,${-outerR}`,
              `A ${outerR},${outerR} 0 1,1 0,${outerR}`,
              `A ${outerR},${outerR} 0 1,1 0,${-outerR}`,
              'Z',
              `M 0,${-innerR}`,
              `A ${innerR},${innerR} 0 1,0 0,${innerR}`,
              `A ${innerR},${innerR} 0 1,0 0,${-innerR}`,
              'Z',
            ].join(' ');

            return (
              <g key={`band-${ring}`} style={{ pointerEvents: 'none' }}>
                <circle
                  cx={0}
                  cy={0}
                  r={canvasRadius}
                  fill={`url(#band-grad-${ring})`}
                />
                <path
                  d={annularPath}
                  fill={ring % 2 === 1 ? 'rgba(148,163,184,0.04)' : 'rgba(100,116,139,0.025)'}
                />
                {innerR > 0 && (
                  <circle
                    cx={0}
                    cy={0}
                    r={innerR}
                    fill="none"
                    stroke="rgba(255,255,255,0.04)"
                    strokeWidth={1.5 * inv}
                  />
                )}
                <circle
                  cx={0}
                  cy={0}
                  r={outerR}
                  fill="none"
                  stroke="rgba(255,255,255,0.04)"
                  strokeWidth={1.5 * inv}
                />
              </g>
            );
          })}

          {/* ── Layer 1: Ring circles ──────────────────────── */}
          {RINGS.map((ring) => {
            const r = getRingRadius(ring, canvasRadius);
            const info = RING_SUCCESS_RATES[ring];
            return (
              <g key={ring}>
                <circle
                  cx={0}
                  cy={0}
                  r={r}
                  fill="none"
                  stroke="#475569"
                  strokeWidth={1.5 * inv}
                  strokeDasharray={ring === 4 ? `${6 * inv} ${3 * inv}` : undefined}
                  opacity={ring === 1 ? 0.6 : 0.35}
                />
                <text
                  x={0}
                  y={-r + 16 * inv}
                  textAnchor="middle"
                  fill="#94a3b8"
                  fontSize={11 * inv}
                  fontWeight={500}
                  style={{ pointerEvents: 'none' }}
                >
                  {info.label} ({info.rate})
                </text>
              </g>
            );
          })}

          {/* ── Layer 2: Axis lines ───────────────────────── */}
          {MAP_AXES.map((axis) => {
            const endpoint = getAxisEndpoint(axis, canvasRadius);
            const label = getAxisLabelPosition(axis, canvasRadius);
            const isAxisHovered = hoveredAxis === axis;
            return (
              <g
                key={axis}
                onMouseMove={(e) => handleAxisMouseMove(axis, e)}
                onMouseLeave={handleAxisMouseLeave}
                style={{ cursor: 'default' }}
              >
                {/* Wider invisible hit area for hover */}
                <line
                  x1={0}
                  y1={0}
                  x2={endpoint.x}
                  y2={endpoint.y}
                  stroke="transparent"
                  strokeWidth={20 * inv}
                />
                <line
                  x1={0}
                  y1={0}
                  x2={endpoint.x}
                  y2={endpoint.y}
                  stroke={isAxisHovered ? '#94a3b8' : '#64748b'}
                  strokeWidth={(isAxisHovered ? 2.5 : 1.5) * inv}
                  opacity={isAxisHovered ? 0.8 : 0.5}
                  style={{ pointerEvents: 'none' }}
                />
                {/* Static axis label at endpoint (always visible) */}
                <text
                  x={label.x}
                  y={label.y}
                  textAnchor={label.anchor}
                  dominantBaseline="central"
                  fill="#e2e8f0"
                  fontSize={14 * inv}
                  fontWeight={700}
                  style={{ pointerEvents: 'none' }}
                >
                  {axis}
                </text>
              </g>
            );
          })}

          {/* ── Layer 3: Core circle ──────────────────────── */}
          <g style={{ pointerEvents: 'none' }}>
            <circle cx={0} cy={0} r={CORE_CIRCLE_RADIUS} fill="#0f172a" fillOpacity={0.7} stroke="#3b82f6" strokeWidth={1.5 * inv} opacity={0.9} />
            <text
              x={0}
              y={0}
              textAnchor="middle"
              dominantBaseline="central"
              fill="rgba(255,255,255,0.18)"
              fontSize={22 * inv}
              fontWeight={700}
              letterSpacing={2}
            >
              CORE
            </text>
          </g>

          {/* ── Layer 4: Connection lines ─────────────────── */}
          <ConnectionLines
            nodes={nodes}
            positions={positions}
            hoveredNodeId={hoveredNodeId}
            selectedNodeId={selectedNodeId}
            selectedEdge={selectedEdge}
            filterPassingIds={filterPassingIds}
            relationshipFilter={filters.relationshipFilter}
            zoomScale={transform.k}
            onEdgeClick={handleEdgeClick}
            onEdgeDoubleClick={handleEdgeDoubleClick}
          />

          {/* ── Layer 5: Nodes ────────────────────────────── */}
          {nodes.map((node) => {
            const pos = positions.get(node.id);
            if (!pos) return null;
            const passesFilter = filterPassingIds.has(node.id);
            const isDragging = dragState?.nodeId === node.id;
            const renderPos = isDragging ? dragState.pos : pos;

            return (
              <g key={node.id}>
                {/* Ghost at original position during drag */}
                {isDragging && (
                  <g transform={`translate(${dragState.origin.x},${dragState.origin.y})`} opacity={0.25} style={{ pointerEvents: 'none' }}>
                    <MapNode
                      node={node}
                      isSelected={false}
                      isHovered={false}
                      passesFilter={passesFilter}
                      zoomLevel={transform.k}
                      onSelect={() => {}}
                      onHoverStart={() => {}}
                      onHoverEnd={() => {}}
                    />
                  </g>
                )}
                <g transform={`translate(${renderPos.x},${renderPos.y})`} style={isDragging ? { cursor: 'grabbing' } : undefined}>
                  <MapNode
                    node={node}
                    isSelected={node.id === selectedNodeId}
                    isHovered={node.id === hoveredNodeId}
                    passesFilter={passesFilter}
                    zoomLevel={transform.k}
                    onSelect={handleNodeClick}
                    onDoubleClick={handleNodeDoubleClick}
                    onContextMenu={handleNodeContextMenu}
                    onDragStart={handleDragStart}
                    onHoverStart={handleHoverStart}
                    onHoverEnd={handleHoverEnd}
                  />
                </g>
              </g>
            );
          })}
        </g>
      </svg>

      {/* ── Search overlay (top center) ────────────────── */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 8,
          padding: '6px 12px',
          minWidth: 260,
        } satisfies CSSProperties}
      >
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--text-dim)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="text"
          value={filters.searchText}
          onChange={(e) => setFilters({ searchText: e.target.value })}
          placeholder="Search map..."
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--text-primary)',
            fontSize: 13,
            width: '100%',
          }}
        />
        {filters.searchText && (
          <span style={{ color: 'var(--text-dim)', fontSize: 11, flexShrink: 0 }}>
            {filterPassingIds.size} found
          </span>
        )}
        {filters.searchText && (
          <button
            onClick={() => setFilters({ searchText: '' })}
            style={{ color: 'var(--text-dim)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 1 }}
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* ── Floating overlays (positioned above SVG) ──── */}
      <MapControls
        zoomLevel={transform.k}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetZoom={resetZoom}
        onFitToContent={handleFitToContent}
        canUndo={_past.length > 0}
        canRedo={_future.length > 0}
        onUndo={() => void undo()}
        onRedo={() => void redo()}
        onAddNode={() => void handleAddNode()}
        onImport={() => setShowBulkImport(true)}
        onExport={handleExport}
        onImportData={handleImportData}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileChange}
      />
      {/* ── Axis hover tooltip (follows pointer) ──────── */}
      {hoveredAxis && axisHoverPos && (
        <div
          className="absolute pointer-events-none z-10"
          style={{
            left: axisHoverPos.x + 14,
            top: axisHoverPos.y - 18,
          }}
        >
          <div
            className="px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap"
            style={{
              background: 'rgba(15, 23, 42, 0.92)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(59, 130, 246, 0.4)',
              color: '#f1f5f9',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4), 0 0 20px rgba(59, 130, 246, 0.1)',
            }}
          >
            {hoveredAxis}
          </div>
        </div>
      )}

      {/* ── Node hover tooltip ─────────────────────────── */}
      {hoveredNodeId && !dragState && !contextMenu && (() => {
        const hNode = nodes.find(n => n.id === hoveredNodeId);
        const hPos = positions.get(hoveredNodeId);
        if (!hNode || !hPos) return null;
        const screenX = hPos.x * transform.k + transform.x;
        const screenY = hPos.y * transform.k + transform.y;
        const initColor = INITIATIVE_COLORS[hNode.strategicInitiative];
        const sColor = hNode.status ? (STATUS_COLORS[hNode.status] ?? STATUS_DEFAULT_COLOR) : STATUS_DEFAULT_COLOR;
        const tipLeft = screenX + 20 + 250 > viewportSize.width ? screenX - 265 : screenX + 20;
        const tipTop = Math.max(8, Math.min(screenY - 20, viewportSize.height - 220));
        return (
          <div
            className="absolute pointer-events-none"
            style={{ left: tipLeft, top: tipTop, zIndex: 15 }}
          >
            <div
              className="w-[240px] rounded-xl overflow-hidden"
              style={{
                background: 'rgba(15, 23, 42, 0.94)',
                backdropFilter: 'blur(16px) saturate(180%)',
                border: `1px solid ${initColor}40`,
                boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 24px ${initColor}15`,
              }}
            >
              {/* Colored accent bar */}
              <div style={{ height: 3, background: `linear-gradient(90deg, ${initColor}, ${initColor}00)` }} />

              {/* Header */}
              <div className="px-3 pt-2.5 pb-1.5">
                <div className="text-[12px] font-bold leading-tight" style={{ color: '#f1f5f9' }}>
                  {hNode.name}
                </div>
                {hNode.description && (
                  <div className="text-[10px] mt-0.5 leading-snug" style={{ color: 'var(--text-muted)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {hNode.description}
                  </div>
                )}
              </div>

              {/* Tags row */}
              <div className="px-3 pb-2 flex flex-wrap items-center gap-1">
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-px rounded-full text-[9px] font-medium"
                  style={{ background: initColor + '22', color: initColor }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: initColor }} />
                  {hNode.strategicInitiative}
                </span>
                {hNode.status && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-px rounded-full text-[9px] font-medium"
                    style={{ background: sColor + '22', color: sColor }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: sColor }} />
                    {hNode.status}
                  </span>
                )}
                {hNode.phase && (
                  <span
                    className="px-1.5 py-px rounded-full text-[9px]"
                    style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-secondary)' }}
                  >
                    {hNode.phase}
                  </span>
                )}
              </div>

              {/* Metadata grid */}
              <div
                className="px-3 py-2 grid grid-cols-3 gap-x-2 gap-y-1 text-[9px]"
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div>
                  <div style={{ color: 'var(--text-dim)' }}>Axis</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{hNode.adjacencyAxis === 'New Technologies & Capabilities' ? 'New Tech' : hNode.adjacencyAxis}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-dim)' }}>Ring</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{hNode.relatednessRing}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-dim)' }}>Priority</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{hNode.priority ?? '—'}</div>
                </div>
                {hNode.attractivenessScore != null && (
                  <div>
                    <div style={{ color: 'var(--text-dim)' }}>Bain Score</div>
                    <div style={{ color: 'var(--text-secondary)' }}>{hNode.attractivenessScore}/10</div>
                  </div>
                )}
                {hNode.edges.length > 0 && (
                  <div>
                    <div style={{ color: 'var(--text-dim)' }}>Links</div>
                    <div style={{ color: 'var(--text-secondary)' }}>{hNode.edges.length}</div>
                  </div>
                )}
              </div>

              {/* Keyboard hints */}
              <div
                className="px-3 py-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[8px]"
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)', color: 'var(--text-dim)' }}
              >
                <span>Click — Select</span>
                <span>Dbl-click — Edit</span>
                <span>Right-click — Menu</span>
                <span>Shift+Drag — Move</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Node context menu ────────────────────────── */}
      {contextMenu && (() => {
        const ctxNode = nodes.find(n => n.id === contextMenu.nodeId);
        if (!ctxNode) return null;
        return (
          <NodeContextMenu
            node={ctxNode}
            x={contextMenu.x}
            y={contextMenu.y}
            containerWidth={viewportSize.width}
            containerHeight={viewportSize.height}
            onClose={closeContextMenu}
            onEdit={(id) => { setDetailNodeId(id); }}
            onDuplicate={(id) => void duplicateNode(id)}
            onChangeStatus={(id, status) => void updateNode(id, { status })}
            onChangePhase={(id, phase) => void updateNode(id, { phase })}
            onAddChild={(id) => { selectNode(id); }}
            onAddRelated={(id) => { selectNode(id); }}
            onResetPosition={(id) => void updateNode(id, { mapPosition: null })}
            onDelete={(id) => {
              const n = nodes.find(nd => nd.id === id);
              if (!n) return;
              setPendingConfirm({
                title: 'Delete Node',
                message: `Delete "${n.name}"? You can undo this with Ctrl+Z.`,
                onConfirm: () => {
                  void deleteNode(id);
                  setPendingConfirm(null);
                },
              });
            }}
          />
        );
      })()}

      {/* ── Node edit modal ──────────────────────────── */}
      {detailNode && (
        <NodeModal
          node={detailNode}
          allNodes={nodes}
          onClose={handleCloseDetail}
          onSave={(id, changes) => void updateNode(id, changes)}
          onDelete={(id) => void deleteNode(id)}
          onDuplicate={(id) => void duplicateNode(id)}
          onAddParent={(parentId, childId) => void createParentChildLink(parentId, childId)}
          onAddChild={(parentId, childId) => void createParentChildLink(parentId, childId)}
          onAddRelated={(a, b) => void createRelatedLink(a, b)}
          onRemoveRelationship={(a, b) => void removeRelationship(a, b)}
          onConvertEdgeType={(a, b, toType, parentId) => void convertEdgeType(a, b, toType, parentId)}
        />
      )}

      {/* ── Line attribute modal ───────────────────────── */}
      {lineEditEdge && (() => {
        const srcNode = nodes.find(n => n.id === lineEditEdge.sourceId);
        const tgtNode = nodes.find(n => n.id === lineEditEdge.targetId);
        const edge = srcNode?.edges.find(e => e.targetId === lineEditEdge.targetId);
        if (!srcNode || !tgtNode || !edge) return null;
        return (
          <LineAttributeModal
            sourceNodeName={srcNode.name}
            targetNodeName={tgtNode.name}
            initial={{ lineStyle: edge.lineStyle, lineColor: edge.lineColor, label: edge.label ?? '' }}
            onSave={handleLineEditSave}
            onClose={() => setLineEditEdge(null)}
          />
        );
      })()}

      {/* ── Bulk import modal ──────────────────────────── */}
      {showBulkImport && (
        <BulkImportModal
          existingNodes={nodes}
          onImport={bulkCreateNodes}
          onClose={() => setShowBulkImport(false)}
        />
      )}

      {/* ── Confirm dialog ─────────────────────────────── */}
      {pendingConfirm && (
        <ConfirmDialog
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          confirmColor={pendingConfirm.confirmColor}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}
